import React, { useContext, useEffect, useState } from 'react';
import {
  Jumbotron,
  Form,
  Input,
  Button,
  PageLoader,
  Notifier,
  notify,
} from '@scholastic/volume-react';

import './MonthlyReports.scss';
import { StoreContext } from '../../state/store';
import { getCustomerInfoFromAPIResponse } from '../Home/Home.dataparser';
import { getMonthlyData, postMonthlyData } from '../../state/MonthlyReport.api';

export interface MonthlyReportData {
  customerEmails?: string[];
  repEmails?: string[];
  skipChildren: boolean;
  isSiteReport: boolean;
  isGroupReport: boolean;
  isSummary: boolean;
}

const MonthlyReports = () => {
  const { state } = useContext(StoreContext);
  const customerInfo = state.customerInfo;

  const [isGroup, setGroupStatus] = useState<boolean>(false);
  const [hasChildren, setChildrenStatus] = useState<boolean>(false);
  const [monthlyReportData, setMonthlyReportData] = useState<MonthlyReportData | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (customerInfo) {
      const parsedCustomerInfo = getCustomerInfoFromAPIResponse(customerInfo);

      setGroupStatus(!!parsedCustomerInfo?.hasgroups);
      setChildrenStatus(!!parsedCustomerInfo?.haschildren);
      fetchMonthlyData(parsedCustomerInfo?.customerid);
    }
  }, [customerInfo]);

  const fetchMonthlyData = async (customerId: any) => {
    setLoading(true);
    try {
      const customerMonthlyData = await getMonthlyData(customerId);
      setMonthlyReportData(customerMonthlyData);
    } catch (error) {
      setMonthlyReportData({
        customerEmails: ['test@test.com', 'abc@xyz.com'],
        repEmails: ['xyz@test.com', 'abc@xyz.com'],
        skipChildren: true,
        isSiteReport: true,
        isGroupReport: true,
        isSummary: true,
      });
      console.error(error);
      notify.warning('Something went wrong, please try again later');
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const parsedCustomerInfo = getCustomerInfoFromAPIResponse(customerInfo);
    postMonthlyData(monthlyReportData, parsedCustomerInfo?.customerid)
      .then(
        (resp) => {
          notify.success('Request successfully submitted');
        },
        (error) => {
          // TODO: notify components should have console logging built in
          console.error(error);
          notify.warning('Please try again');
        },
      )
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="stats-monthly-reports">
      <Notifier
        autoClose={5000}
        closeButton={false}
        closeOnClick={false}
        draggable
        draggablePercent={50}
        limit={1}
        newestOnTop
        pauseOnFocusLoss
        pauseOnHover
        position="top-center"
        role="alert"
      />
      <div className="stats-monthly-reports__text-container stats-monthly-reports__text-container--nobackground">
        {loading && <PageLoader color={'dark'} size={'large'} type={'border'} />}

        <h1 className="stats-monthly-reports__title">Sign up for Monthly Reports</h1>

        <Jumbotron tag="div">
          <p className="stats-monthly-reports--normal-font">
            Fill in the form below to receive an e-mail containing your Scholastic Online product
            statistics on a monthly basis in Excel format. Reports will be e-mailed during the first
            week of each month and will contain the usage information for the previous month.
          </p>
        </Jumbotron>

        <div>
          <div className="stats-monthly-reports__check-box-holder stats-monthly-reports__check-box-holder--full-screen">
            <span className="stats-monthly-reports__check-box-label">
              <input
                type="checkbox"
                checked={monthlyReportData?.isSummary}
                name="SRISU"
                id="SRISU"
                data-testid="SRISU"
                onChange={(event) => {
                  const data = { ...monthlyReportData } as MonthlyReportData;

                  if ((event.currentTarget as HTMLInputElement).checked === true) {
                    data.isSummary = true;
                  } else {
                    data.isSummary = false;
                  }

                  setMonthlyReportData(data);
                }}
              />
            </span>
            <span className="stats-monthly-reports__check-box-label">
              Summary Report with Individual Site Usage
            </span>
          </div>

          {hasChildren && (
            <div className="stats-monthly-reports__check-box-holder stats-monthly-reports__check-box-holder--full-screen">
              <span className="stats-monthly-reports__check-box-label">
                <input
                  type="checkbox"
                  checked={monthlyReportData?.skipChildren}
                  name="SRWISU"
                  id="SRWISU"
                  data-testid="SRWISU"
                  onChange={(event) => {
                    const data = { ...monthlyReportData } as MonthlyReportData;

                    if ((event.currentTarget as HTMLInputElement).checked === true) {
                      data.skipChildren = true;
                    } else {
                      data.skipChildren = false;
                    }

                    setMonthlyReportData(data);
                  }}
                />
              </span>
              <span className="stats-monthly-reports__check-box-label">
                Summary Report without Individual Site Usage
              </span>
            </div>
          )}

          {hasChildren && (
            <div className="stats-monthly-reports__check-box-holder stats-monthly-reports__check-box-holder--full-screen">
              <span className="stats-monthly-reports__check-box-label">
                <input
                  type="checkbox"
                  checked={monthlyReportData?.isSiteReport}
                  name="ISR"
                  id="ISR"
                  data-testid="ISR"
                  onChange={(event) => {
                    const data = { ...monthlyReportData } as MonthlyReportData;

                    if ((event.currentTarget as HTMLInputElement).checked === true) {
                      data.isSiteReport = true;
                    } else {
                      data.isSiteReport = false;
                    }
                    setMonthlyReportData(data);
                  }}
                />
              </span>
              <span className="stats-monthly-reports__check-box-label">Individual Site Report</span>
            </div>
          )}

          {isGroup && (
            <div className="stats-monthly-reports__check-box-holder stats-monthly-reports__check-box-holder--full-screen">
              <span className="stats-monthly-reports__check-box-label">
                <input
                  type="checkbox"
                  checked={monthlyReportData?.isGroupReport}
                  name="UGR"
                  id="UGR"
                  data-testid="UGR"
                  onChange={(event) => {
                    const data = { ...monthlyReportData } as MonthlyReportData;

                    if ((event.currentTarget as HTMLInputElement).checked === true) {
                      data.isGroupReport = true;
                    } else {
                      data.isGroupReport = false;
                    }
                    setMonthlyReportData(data);
                  }}
                />
              </span>
              <span className="stats-monthly-reports__check-box-label">User Groups Report</span>
            </div>
          )}
        </div>

        <div className="stats-monthly-reports__option-label">
          Please enter an e-mail address, or multiple addresses separated by commas, below. At least
          one valid e-mail address is required.
        </div>

        <Form>
          <Input
            id="emails"
            data-testid="emails"
            className="stats-monthly-reports__textarea"
            layout={undefined}
            name="Emails"
            onChange={(e) => {
              const data = { ...monthlyReportData } as MonthlyReportData;
              data.customerEmails = e.target.value.split(',');
              setMonthlyReportData(data);
            }}
            placeholder="Please enter an e-mail address, or multiple addresses separated by commas"
            type="textarea"
            value={monthlyReportData?.customerEmails?.join(',')}
          />

          <div className="stats-monthly-reports__option-label">
            <b>Sales reps</b> please enter your e-mail address here:
          </div>

          <Input
            className="stats-monthly-reports__textarea"
            layout={undefined}
            id="salesRepEmail"
            name="salesRepEmail"
            data-testid="salesRepEmail"
            onChange={(e) => {
              const data = { ...monthlyReportData } as MonthlyReportData;
              data.repEmails = e.target.value.split(',');
              setMonthlyReportData(data);
            }}
            placeholder="Please enter Sales Rep e-mail here"
            value={monthlyReportData?.repEmails?.join(',')}
          />
        </Form>

        <div className="float-right">
          <Button
            id="submit"
            data-testid="submit"
            color="info"
            className="mt-2"
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MonthlyReports;
